import { useCallback } from 'react';
import { useSessionstorageState } from 'rooks';

import { APP_ROUTES } from 'routes/routePaths';

export enum SessionStateKeys {
  BREAKOUT_ROOM_ID = 'breakout-room-id',
  MATRIX_REDIRECT_TO_AUTH = 'matrix-redirect-to-auth',
  IS_SESSION_LOGGED_OUT = 'is-session-logged-out',
  NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP = 'needs-security-code-prompt-after-bootstrap',
  /** Set before SSO redirect on Matrix soft logout; consumed on token login return. */
  MATRIX_SOFT_LOGOUT_RECOVERY = 'matrix-soft-logout-recovery',
  /** Prevents duplicate logout toasts when auth-failure handling races across effects / remounts. */
  AUTH_FAILURE_LOGOUT_TOAST_SHOWN = 'matrix-auth-failure-logout-toast-shown',
  /**
   * Captured before a hard logout clears the `connect_matrixLoggedIn` cookie, so the guest
   * registration path can redirect a former SSO user to `/auth` instead of silently
   * downgrading them to a guest account (AC-006 / OD-2). Consumed once via getAndRemove.
   */
  AUTH_HAD_SSO_SESSION = 'auth-had-sso-session',
  CHAT_LIST_SECURITY_CODE_PROMPT_DISMISSED = 'chat-list-security-code-prompt-dismissed',
  // BUG-028: origin path captured when a user opens a meeting pre-room from a
  // messenger chat, so the pre-room "Go back" button returns to that chat rather
  // than defaulting to /video (scheduled-meeting rooms carry no MESSENGER_CALL_STATE).
  PRE_ROOM_BACK_PATH = 'pre-room-back-path',
  /**
   * Set by `useStartMatrixClient` when a pre_sync reset was deferred and the account is healthy
   * (non-poisoned). `masAlreadyAuthorised` cannot complete the UIA on a standard-UIA server — the
   * session ID was not allocated when the user first went through MAS. `PostLoginResetGate` reads
   * this flag and re-opens `ResetKeysModal` after login so the user can complete a fresh reset with
   * the client started and a properly session-scoped MAS authorisation (BUG-015 Fix B).
   */
  NEEDS_RESET_KEYS_AFTER_LOGIN = 'needs-reset-keys-after-login',
}

const parseSessionValue = <T>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    console.warn('Failed to parse session storage value:', error);
    return null;
  }
};

export const setSessionStorageValue = <T = unknown>(key: string, value: T) =>
  sessionStorage.setItem(key, JSON.stringify(value));

export const removeSessionStorageValue = (key: string) => sessionStorage.removeItem(key);

export const getAndRemoveSessionStorageValue = <T = unknown>(key: string): T | null => {
  const storedValue = sessionStorage.getItem(key);
  if (storedValue === null) return null;

  sessionStorage.removeItem(key);

  return parseSessionValue<T>(storedValue);
};

export const getSessionStorageValue = <T = unknown>(key: string): T | null =>
  parseSessionValue<T>(sessionStorage.getItem(key));

/**
 * BUG-028: records the messenger chat a meeting was opened from, so the pre-room
 * "Go back" button can return there. Call this immediately before navigating to
 * `/rooms/<callRoomId>` from any messenger-context entry point (sidebar / banner /
 * chat row / meeting card). Consumed once by `useBackToOriginPath`.
 */
export const setPreRoomBackPath = (messengerRoomId: string): void =>
  setSessionStorageValue(SessionStateKeys.PRE_ROOM_BACK_PATH, `/messenger/${messengerRoomId}`);

/** Session key prefix for per-room pre-joined waiting state (see SfuConnectionStateProvider). */
export const PRE_JOINED_SESSION_PREFIX = 'pre-joined';

const REQUIRE_PRE_ROOM_LOBBY_PREFIX = 'require-pre-room-lobby';

export const preJoinedSessionKey = (callRoomId: string): string => `${PRE_JOINED_SESSION_PREFIX}-${callRoomId}`;

const requirePreRoomLobbyKey = (callRoomId: string): string => `${REQUIRE_PRE_ROOM_LOBBY_PREFIX}-${callRoomId}`;

/**
 * Chat schedule-card join: drop stale pre-joined state and require the pre-meeting
 * lobby on the next room load (blocks skip-lobby / instant auto-join until the user
 * clicks Join on ScheduledRoomJoinForm).
 */
export const resetScheduledMeetingJoinFromChat = (callRoomId: string): void => {
  removeSessionStorageValue(preJoinedSessionKey(callRoomId));
  setSessionStorageValue(requirePreRoomLobbyKey(callRoomId), true);
};

/** One-shot read — consumed when Room.tsx mounts for the call room. */
export const consumeRequirePreRoomLobby = (callRoomId: string): boolean => {
  const key = requirePreRoomLobbyKey(callRoomId);
  const required = getSessionStorageValue<boolean>(key) === true;
  if (required) removeSessionStorageValue(key);
  return required;
};

/**
 * #8073: records the video hub as the origin a meeting was opened from, so the pre-room
 * "Go back" button returns to `/meetings` instead of inferring a messenger chat from the
 * room hierarchy. Call this immediately before navigating to `/rooms/<callRoomId>` from any
 * video hub entry point (meeting row / detail header / join-by-id empty state / instant
 * create). Consumed once by `useBackToOriginPath`.
 */
export const setPreRoomBackPathToVideoHub = (): void =>
  setSessionStorageValue(SessionStateKeys.PRE_ROOM_BACK_PATH, APP_ROUTES.meetings);

export const dispatchSessionStorageUpdateEvent = <T = unknown>(key: string, newValue: T) => {
  const eventToDispatch = new CustomEvent(`rooks-${key}-sessionstorage-update`, {
    detail: { newValue },
  });

  document.dispatchEvent(eventToDispatch);
};

/**
 * Lightweight sessionStorage hook backed by rooks' useSessionstorageState.
 *
 * - Returns a tuple `[value, setValue, removeValue]` similar to `useState`
 * - Keeps data persisted in sessionStorage and synced across hook instances/tabs
 * - `setValue(null)` stores `null` explicitly; call `removeValue()` (or helpers)
 *   to delete the key entirely
 * - `initialValue` seeds the storage entry if it doesn't exist yet
 * - Be aware that if you don't pass initial value, it will initialize as undefined
 */
export const useSessionState = <T>(key: string, initialValue: T | null = null) => {
  const [value, setValue, remove] = useSessionstorageState<T | null>(
    key,
    initialValue === null ? undefined : initialValue
  );

  const removeState = useCallback(() => remove(), [remove]);

  return [value ?? null, setValue, removeState] as const;
};
